import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const GeneralInformation = ({ asset, isEditing, onUpdate }) => {
  const assetTypeOptions = [
    { value: 'laptop', label: 'Laptop' },
    { value: 'desktop', label: 'Desktop Computer' },
    { value: 'server', label: 'Server' },
    { value: 'printer', label: 'Printer' },
    { value: 'monitor', label: 'Monitor' },
    { value: 'tablet', label: 'Tablet' },
    { value: 'phone', label: 'Phone' },
    { value: 'network', label: 'Network Equipment' },
    { value: 'other', label: 'Other' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'spare', label: 'Spare' },
    { value: 'repair', label: 'Under Repair' },
    { value: 'retired', label: 'Retired' }
  ];

  const conditionOptions = [
    { value: 'excellent', label: 'Excellent' },
    { value: 'good', label: 'Good' },
    { value: 'fair', label: 'Fair' },
    { value: 'poor', label: 'Poor' }
  ];

  const handleFieldChange = (field, value) => {
    onUpdate({ ...asset, [field]: value });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
      <h3 className="text-lg font-semibold text-foreground mb-6">General Information</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-4">
          <Input
            label="Asset Name"
            type="text"
            value={asset?.name || ''}
            onChange={(e) => handleFieldChange('name', e?.target?.value)}
            disabled={!isEditing}
            required
            placeholder="Enter asset name"
          />

          <Input
            label="Asset ID"
            type="text"
            value={asset?.assetId || ''}
            onChange={(e) => handleFieldChange('assetId', e?.target?.value)}
            disabled={!isEditing}
            required
            placeholder="Auto-generated ID"
            description="Unique identifier for this asset"
          />

          <Input
            label="Serial Number"
            type="text"
            value={asset?.serialNumber || ''}
            onChange={(e) => handleFieldChange('serialNumber', e?.target?.value)}
            disabled={!isEditing}
            placeholder="Enter serial number"
          />

          <Select
            label="Asset Type"
            options={assetTypeOptions}
            value={asset?.assetType || ''}
            onChange={(value) => handleFieldChange('assetType', value)}
            disabled={!isEditing}
            required
            placeholder="Select asset type"
          />

          <Input
            label="Brand/Manufacturer"
            type="text"
            value={asset?.brand || ''}
            onChange={(e) => handleFieldChange('brand', e?.target?.value)}
            disabled={!isEditing}
            placeholder="Enter brand name"
          />

          <Input
            label="Model"
            type="text"
            value={asset?.model || ''}
            onChange={(e) => handleFieldChange('model', e?.target?.value)}
            disabled={!isEditing}
            placeholder="Enter model number"
          />
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          <Select
            label="Current Status"
            options={statusOptions}
            value={asset?.status || ''}
            onChange={(value) => handleFieldChange('status', value)}
            disabled={!isEditing}
            required
            placeholder="Select status"
          />

          <Select
            label="Physical Condition"
            options={conditionOptions}
            value={asset?.condition || ''}
            onChange={(value) => handleFieldChange('condition', value)}
            disabled={!isEditing}
            placeholder="Select condition"
          />

          <Input
            label="Purchase Date"
            type="date"
            value={asset?.purchaseDate || ''}
            onChange={(e) => handleFieldChange('purchaseDate', e?.target?.value)}
            disabled={!isEditing}
            required
          />

          <Input
            label="Warranty Expiration"
            type="date"
            value={asset?.warrantyExpiration || ''}
            onChange={(e) => handleFieldChange('warrantyExpiration', e?.target?.value)}
            disabled={!isEditing}
          />

          <Input
            label="Description"
            type="text"
            value={asset?.description || ''}
            onChange={(e) => handleFieldChange('description', e?.target?.value)}
            disabled={!isEditing}
            placeholder="Brief description of the asset"
          />

          <div className="space-y-3">
            <Checkbox
              label="Critical Asset"
              checked={asset?.isCritical || false}
              onChange={(e) => handleFieldChange('isCritical', e?.target?.checked)}
              disabled={!isEditing}
              description="Mark as business-critical asset"
            />

            <Checkbox
              label="Requires Special Handling"
              checked={asset?.requiresSpecialHandling || false}
              onChange={(e) => handleFieldChange('requiresSpecialHandling', e?.target?.checked)}
              disabled={!isEditing}
              description="Asset needs special care during transport"
            />

            <Checkbox
              label="Include in Audit"
              checked={asset?.includeInAudit !== false}
              onChange={(e) => handleFieldChange('includeInAudit', e?.target?.checked)}
              disabled={!isEditing}
              description="Include this asset in quarterly audits"
            />
          </div>
        </div>
      </div>
      {/* Additional Notes */}
      <div className="mt-6">
        <label className="block text-sm font-medium text-foreground mb-2">
          Additional Notes
        </label>
        <textarea
          value={asset?.notes || ''}
          onChange={(e) => handleFieldChange('notes', e?.target?.value)}
          disabled={!isEditing}
          rows={4}
          className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
          placeholder="Enter any additional notes about this asset..."
        />
      </div>
    </div>
  );
};

export default GeneralInformation;